
// `const` y no `var`: lo que se exporta aquí es el objeto, y un `var` exportado deja además la
// vinculación mutable desde fuera del módulo (Sonar `javascript:S6861`).
const FileUtils = {
    /**
     * Escribe el blob en la entrada **sustituyendo** lo que hubiera, y es el único sitio por el que
     * pasan las escrituras de fichero completo: aquí, y en `Utils.writeFile`, `Documento.saveBlob` y
     * la reescritura del EXIF, que la llaman.
     *
     * Hace falta porque `FileWriter.write()` **no vacía la entrada**: escribe desde la posición
     * actual y deja intacto lo que sobre por detrás. Guardar encima de un fichero que ya existía con
     * un contenido más corto dejaba pegada la cola del anterior, y eso no es un fichero más grande:
     * es otro documento. En el PDF del control diario, el visor podía abrir la versión vieja (#173,
     * ronda 3 de la revisión); en el respaldo de una inspección, un JSON que ya no se puede leer.
     *
     * El `truncate(0)` previo es el mismo que ya hacía `loki-cordova-fs-adapter.js` para guardar la
     * base local, que es el único sitio del repo donde esto estaba bien resuelto.
     *
     * `hecho` recibe el `fileEntry`, para que quien llame no tenga que arrastrarlo por el cierre.
     *
     * De aquí sale **una sola respuesta y siempre una**: o `hecho` o `onError`, una vez. Hace falta
     * decirlo porque `writeend` no significa «ha ido bien» sino «ha terminado»: una operación que
     * falla dispara primero `error` y **después** `writeend`
     * (https://www.w3.org/TR/file-writer-api/). Sin distinguirlos pasaban las dos cosas que esto
     * venía a evitar (#173, ronda 4 de la revisión): el truncado que fallaba seguía escribiendo
     * encima, que es otra vez el fichero mezclado, y la escritura que fallaba llamaba a `hecho`, así
     * que `openFile` abría el PDF viejo justo después de haber avisado del fallo.
     */
    writeFull: function(fileEntry, blob, hecho, onError) {
        fileEntry.createWriter(function (fileWriter) {
            let respondido = false;
            const fallo = function(e) {
                if (respondido) return;
                respondido = true;
                onError(e);
            };
            // Si la operación falló, la cadena se corta aquí. Y se avisa igual: el `error` llega
            // antes que el `writeend`, pero si alguna implementación solo dejara el `error` puesto
            // en el escritor, quien exporta se quedaría esperando para siempre.
            const haFallado = function() {
                if (respondido) return true;
                if (fileWriter.error) {
                    fallo(fileWriter.error);
                    return true;
                }
                return false;
            };
            const escribir = function() {
                fileWriter.onwriteend = function() {
                    if (haFallado()) return;
                    respondido = true;
                    hecho(fileEntry);
                };
                fileWriter.write(blob);
            };
            fileWriter.onerror = fallo;
            // Una entrada recién creada ya está vacía: truncarla sería una escritura de más.
            if (fileWriter.length) {
                // El `truncate` también termina en `onwriteend`, así que la escritura de verdad va
                // encadenada ahí: lanzarla antes la haría sobre la longitud sin recortar todavía.
                fileWriter.onwriteend = function() {
                    if (haFallado()) return;
                    escribir();
                };
                fileWriter.truncate(0);
            } else {
                escribir();
            }
        }, onError);
    },
    saveFile: function(data, path, nombre, extension, resolve, reject) {
        const self = this;
        try {
            var baseDir = typeof eapp != "undefined" ? eapp.getPath("userData") : "";
            let dir = baseDir + path;
            if (typeof fs != "undefined") {
                data = new Buffer(data);
                var filePath = baseDir + path + nombre + '.' + extension;
                if (!fs.existsSync(dir)) fs.mkdirSync(dir);
                fs.writeFile(filePath, data, 'binary', function(err) {
                    if (err) reject({ error: 'Error almacenando imágenes' });
                    else resolve();
                });
            } else {
                data = new Blob([data]);
                var filePath = nombre + '.' + extension;
                var onError = function(e) {
                    reject({ error: 'Error almacenando imágenes: ' + e.toString() });
                };
                // Fuera del anidamiento a propósito: dentro quedaba a cinco niveles de funciones,
                // que es lo que avisa Sonar (`javascript:S2004`), y aquí se lee igual.
                const guardado = function() {
                    resolve();
                };
                window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, function (fs) {
                    fs.root.getDirectory(dir, { create: true }, function (dirEntry) {

                        dirEntry.getFile(filePath, { create: true, exclusive: false }, function (fileEntry) {
                            self.writeFull(fileEntry, data, guardado, onError);
                        }, onError);
                    }, onError);
                }, onError);
            }
        } catch (err) {
            reject({ error: 'Error almacenando imágenes: No se pudo crear el directorio local' });
            console.log(err);
            return;
        }
    },
    saveWithoutOverride (base64Data, path, nombreOrig, extension, resolve, reject) {
        var self = this;
        try {
            if (typeof fs != "undefined") {
                var n = 0;
                var baseDir = typeof eapp != "undefined" ? eapp.getPath("userData") : "";
                let dir = baseDir + path;
                var ruta = baseDir + path + nombre + '.' + extension;
                var nombre = nombreOrig;
                if (!fs.existsSync(dir)) fs.mkdirSync(dir);
                while (fs.existsSync(ruta)) {
                    n++;
                    var nombre = nombre + ' (' + n + ')';
                    var ruta = baseDir + path + nombre + '.' + extension;
                }
                fs.writeFile(baseDir + path + nombre + '.' + extension, base64Data, 'base64', function(err) {
                    if (err) reject({ error: 'Error almacenando documento' });
                    else resolve({
                        nombre: nombreOrig,
                        extension: extension
                    });
                });
            
            } else {
                var data = self.b64toBlob(base64Data);
                self.saveBlobFile(data, nombreOrig, extension, path, resolve, reject);
            }
        } catch (err) {
            reject({ error: 'Error almacenando documentos: No se pudo crear el directorio local' });
            console.log(err);
            return;
        }
    },
    saveBlobFile: function(blob, nombreOrig, extension, path, resolve, reject, override = false) {
        const self = this;
        var nombre = nombreOrig;
        var baseDir = typeof eapp != "undefined" ? eapp.getPath("userData") : "";
        path = path || "/documentos/"
        let dir = baseDir + path;
        var n = 0;
        var onError = function(e) {
            reject({ error: 'Error almacenando imágenes: ' + e.toString() });
        };
        var getFile = function (dirEntry, nombreOrig, nombre) {
            var filePath = nombre + '.' + extension;
            const guardado = function (fileEntry) {
                resolve({
                    nombre: nombre,
                    extension: extension,
                    fileEntry: fileEntry,
                    alreadySaved: true
                });
            };
            dirEntry.getFile(filePath, { create: false }, function (fileEntry) {
                // Fichero Existe
                if (override) {
                    self.writeFull(fileEntry, blob, guardado, onError);
                } else {
                    n++;
                    var nombreNuevo = nombre + ' (' + n + ')';
                    getFile(dirEntry, nombreOrig, nombreNuevo);
                }
            }, function () {
                dirEntry.getFile(filePath, { create: true, exclusive: false }, function (fileEntry) {
                    self.writeFull(fileEntry, blob, guardado, onError);
                }, onError);
            });
        }
        window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, function (fs) {
            fs.root.getDirectory(dir, { create: true }, function (dirEntry) {
                getFile(dirEntry, nombreOrig, nombre);
            }, onError);
        }, onError);
    },
    b64toBlob: function(b64Data, contentType, sliceSize) {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;
        b64Data = b64Data.replace(/^data:\w+\/\w+;base64,/, "");
        var byteCharacters = atob(b64Data);
        var byteArrays = [];

        for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            var slice = byteCharacters.slice(offset, offset + sliceSize);

            var byteNumbers = new Array(slice.length);
            for (var i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            var byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        var blob = new Blob(byteArrays, {type: contentType});
        return blob;
    },
    b64toData: function(b64Data, contentType, sliceSize) {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;

        var byteCharacters = atob(b64Data);
        var byteArrays = [];

        for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            var slice = byteCharacters.slice(offset, offset + sliceSize);

            var byteNumbers = new Array(slice.length);
            for (var i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            var byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return byteArrays;
    },
    /**
     * Escribe el fichero y lo abre en el visor del dispositivo.
     *
     * `sigueVigente` es OPCIONAL y solo lo pasa quien tiene algo que perder si deja de estarlo:
     * **una función que se pregunta justo antes de cada paso que no se puede deshacer** —escribir en
     * el dispositivo y abrir el visor—. Si contesta que no, aquí no se escribe ni se abre nada, lo
     * que ya estuviera escrito se borra, y se rechaza con `{descartado: true}` para que quien llamó
     * sepa que no es un fallo que haya que contarle a nadie. El rechazo llega **cuando ese borrado
     * ha terminado**, que es lo que impide que la operación siguiente sobre el mismo fichero empiece
     * con el borrado del anterior todavía en marcha.
     *
     * Existe porque el caso lo tiene la exportación del PDF del control diario (#173): entre que la
     * respuesta llega y el visor se abre hay cuatro pasos asíncronos, y en ese rato se puede cerrar
     * la sesión o entrar otra persona. Preguntarlo una sola vez antes de llamar aquí no sirve: lo
     * irreversible pasa después. Y preguntarlo DENTRO es lo único que cierra el hueco, porque es
     * aquí donde están los pasos.
     *
     * Quien no lo pasa se comporta exactamente igual que antes: sin guardián, siempre vigente.
     */
    openFile: function(data, name, resolve, reject, sigueVigente) {
        const self = this;
        // Único para las dos ramas: quien pidió el fichero tiene que enterarse de que no sale,
        // venga el fallo de donde venga.
        const onError = function(e) {
            reject({ error: 'Error abriendo fichero: ' + ((e && e.toString()) || 'motivo desconocido') });
        };
        const vigente = typeof sigueVigente === 'function' ? sigueVigente : function() { return true; };
        // El descarte NO es un error: no lleva texto que enseñar, porque quien está delante no pidió
        // este fichero. `entrada` es la del fichero ya escrito, si se había llegado a escribir: se
        // borra para que no se quede en el dispositivo un documento de otra sesión.
        //
        // **Y se contesta cuando el borrado ha TERMINADO, no al pedirlo** (#173, ronda 4 del
        // 09/10/2026). `remove` es asíncrono, y quien llama suelta el bloqueo de ese mes en cuanto
        // esta promesa se rompe: contestando antes, la exportación siguiente del mismo mes volvía a
        // crear la entrada —el nombre del fichero es el del mes y se repite— y el borrado que
        // seguía en marcha se llevaba la entrada recién reutilizada, así que la escritura nueva
        // fallaba o se abría un documento que ya no estaba. Mientras no se conteste, el mes sigue
        // bloqueado y no hay dos operaciones sobre el mismo fichero.
        //
        // `borrado` dice si el dispositivo se queda limpio: `true` también cuando no había nada
        // escrito. Si el borrado falla, el PDF de la sesión anterior se queda ahí y eso no se da por
        // hecho —se apunta en la consola—, pero el descarte se contesta igual: a quien está delante
        // no hay nada que contarle, porque este documento no es suyo.
        const descartado = function(entrada) {
            const rechazar = function(borrado) {
                reject({
                    descartado: true,
                    borrado: borrado,
                    error: 'Fichero descartado: ya no es de quien lo pidió.'
                });
            };
            if (!entrada || typeof entrada.remove !== 'function') return rechazar(true);
            return entrada.remove(function() { rechazar(true); }, function(e) {
                console.error('El fichero descartado se queda en el dispositivo: ' + name, e);
                rechazar(false);
            });
        };
        if (typeof FileType != "undefined") {
            // El `catch` NO es de adorno: sin él, un buffer cuyo tipo no se reconoce dejaba esta
            // promesa sin resolver ni rechazar, y quien exporta se queda en «Generando…» para
            // siempre. `t` puede venir vacío por lo mismo, y entonces el tipo lo pone el visor.
            FileType.fromBuffer(data).then(function(t) {
                // Reconocer el tipo es asíncrono, así que el guardián se pregunta DESPUÉS: la
                // descarga es el paso irreversible y es el de la línea siguiente.
                if (!vigente()) return descartado(null);
                const blob = new Blob([data], { type: (t && t.mime) || '' });
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, name);
                } else {
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = name;
                    link.target = '_system';
                    link.click();
                    URL.revokeObjectURL(link.href);
                }
                return resolve();
            }).catch(onError);
        } else {

            const blob = new Blob([data]);
            // La entrada del fichero, para poder borrarlo si hay que descartarlo más adelante: el
            // `File` que da `entrada.file(...)` es el CONTENIDO y no sabe borrarse, así que la
            // entrada hay que guardarla.
            let escrita = null;
            // Los dos pasos de después de escribir, fuera del anidamiento: dentro quedaban a cinco
            // niveles de funciones (Sonar `javascript:S2004`).
            const abrir = function (file) {
                // Último paso y el más visible: el visor del dispositivo enseña el documento a quien
                // tenga el móvil en la mano, sea quien sea.
                if (!vigente()) return descartado(escrita);
                return cordova.plugins.fileOpener2.open(
                    file.localURL,
                    file.type,
                    {
                    onError,
                    success : function(){
                        resolve();
                    }
                    }
                );
            };
            const escrito = function (entrada) {
                escrita = entrada;
                // Escribir es asíncrono: si la sesión ha cambiado mientras se escribía, el fichero
                // está en el dispositivo y hay que quitarlo antes de rechazar.
                if (!vigente()) return descartado(entrada);
                return entrada.file(abrir, onError);
            };
            window.requestFileSystem(window.TEMPORARY, 5 * 1024 * 1024, function (fs) {
                // El nombre lo pone quien llama y se repite entre descargas del mismo documento, así
                // que esta entrada casi siempre existe ya: el `writeFull` la vacía antes de escribir.
                fs.root.getFile(name, { create: true}, function (fileEntry) {
                    // Antes de escribir. La entrada se pasa igual: `create:true` la acaba de crear
                    // vacía, y si ya existía es el PDF del mismo mes que dejó la sesión anterior, que
                    // es justo lo que no debe quedarse en el dispositivo.
                    if (!vigente()) return descartado(fileEntry);
                    return self.writeFull(fileEntry, blob, escrito, onError);
                }, onError);
            }, onError);
        }
    },
    deleteFile: function(name, resolve, reject) {
        var onError = function(e) {
            console.log(e);
            console.log("No se ha podido eliminar el archivo: " + name);
            reject({ error: 'Error borrando archivos: ' + e.toString()});
        };
        window.requestFileSystem(window.PERSISTENT, 5 * 1024 * 1024, function (fs) {
            console.log("Dentro fileSystem");
            console.log(fs);
            fs.root.getFile(name, { create: false}, function (fileEntry) {
                console.log("Dentro getFile");
                console.log(fileEntry);
                fileEntry.remove(function(a){ 
                    console.log("Dentro remove");
                    console.log(a);
                    console.log("Eliminado archivo: " + name);
                    resolve(a);
                }, onError );
            }, onError);
        }, onError);
    }
}

export default FileUtils;